import { useState } from "react";
import { Minus, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export const BookingForm = ({ event, onSuccess }) => {
  const categories = (event.ticket_categories || []).filter((c) => c.quantity - (c.sold || 0) > 0);
  const [categoryId, setCategoryId] = useState(categories[0]?.id || "");
  const [qty, setQty] = useState(1);
  const [firstName, setFirstName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const category = categories.find((c) => c.id === categoryId);
  const available = category ? category.quantity - (category.sold || 0) : 0;
  const total = category ? category.price * qty : 0;

  if (!categories.length) {
    return (
      <div className="card-dark p-8 text-center" data-testid="booking-soldout">
        <p className="font-serif text-xl text-[#E5C158]">Tickets coming soon</p>
        <p className="mt-2 text-sm text-zinc-400">No tickets are currently available for this event.</p>
      </div>
    );
  }

  const finalize = async (booking_id, payload) => {
    const { data } = await api.post("/bookings/verify", { booking_id, ...payload });
    toast.success("Payment successful! Ticket generated.");
    onSuccess(data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !mobile.trim()) {
      toast.error("Please enter your name and mobile number");
      return;
    }
    if (!/^\d{7,15}$/.test(mobile.trim())) {
      toast.error("Enter a valid mobile number");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/bookings/create-order", {
        event_id: event.id, category_id: categoryId, quantity: qty,
        first_name: firstName, mobile, email,
      });

      if (data.razorpay_enabled) {
        const ok = await loadRazorpay();
        if (!ok) { toast.error("Could not load payment gateway"); setLoading(false); return; }
        const rzp = new window.Razorpay({
          key: data.razorpay_key_id,
          amount: Math.round(data.amount * 100),
          currency: "INR",
          name: "Sirakh Creations",
          description: event.name,
          order_id: data.order_id,
          prefill: { name: firstName, contact: mobile, email },
          theme: { color: "#D4AF37" },
          handler: async (res) => {
            try {
              await finalize(data.booking_id, {
                razorpay_payment_id: res.razorpay_payment_id,
                razorpay_order_id: res.razorpay_order_id,
                razorpay_signature: res.razorpay_signature,
              });
            } catch { toast.error("Verification failed"); }
            setLoading(false);
          },
          modal: { ondismiss: () => setLoading(false) },
        });
        rzp.open();
      } else {
        // Mock mode
        await finalize(data.booking_id, {});
        setLoading(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed");
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="card-dark p-6 sm:p-8" data-testid="booking-form">
      <h3 className="font-serif text-2xl font-bold text-white">Book Your Tickets</h3>
      <p className="mt-1 text-sm text-zinc-400">Secure your spot in a few taps.</p>

      <div className="mt-6 space-y-5">
        <div>
          <label className="caption mb-2 block">Ticket Category</label>
          <div className="grid gap-2">
            {categories.map((c) => {
              const avail = c.quantity - (c.sold || 0);
              const selected = c.id === categoryId;
              return (
                <button type="button" key={c.id}
                  onClick={() => { setCategoryId(c.id); setQty(1); }}
                  className={`flex items-center justify-between rounded-md border px-4 py-3 text-left transition-colors ${selected ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-zinc-800 bg-[#0A0A0C]"}`}
                  data-testid={`ticket-category-${c.id}`}>
                  <div>
                    <p className="font-medium text-white">{c.name}</p>
                    <p className="text-xs text-zinc-500">{avail} left</p>
                  </div>
                  <p className="font-serif text-lg font-bold text-[#E5C158]">₹{c.price}</p>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="caption mb-2 block">Quantity</label>
          <div className="flex w-fit items-center gap-4 rounded-md border border-zinc-800 bg-[#0A0A0C] p-2">
            <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))}
              className="flex h-9 w-9 items-center justify-center rounded text-[#E5C158] hover:bg-[#D4AF37]/10" data-testid="qty-minus"><Minus size={16} /></button>
            <span className="w-8 text-center text-lg font-semibold text-white" data-testid="qty-value">{qty}</span>
            <button type="button" onClick={() => setQty((q) => Math.min(available, q + 1))}
              className="flex h-9 w-9 items-center justify-center rounded text-[#E5C158] hover:bg-[#D4AF37]/10" data-testid="qty-plus"><Plus size={16} /></button>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="caption mb-2 block">First Name *</label>
            <input className="input-field" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Your name" data-testid="customer-name-input" />
          </div>
          <div>
            <label className="caption mb-2 block">Mobile Number *</label>
            <input className="input-field" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="10-digit mobile" inputMode="numeric" data-testid="customer-phone-input" />
          </div>
        </div>
        <div>
          <label className="caption mb-2 block">Email (optional)</label>
          <input className="input-field" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" type="email" data-testid="customer-email-input" />
        </div>

        <div className="flex items-center justify-between border-t border-[#2A2A32] pt-5">
          <div>
            <p className="text-xs uppercase tracking-wider text-zinc-500">Total</p>
            <p className="font-serif text-3xl font-bold text-[#E5C158]" data-testid="booking-total">₹{total}</p>
          </div>
          <button type="submit" disabled={loading} className="btn-gold min-w-[180px]" data-testid="proceed-payment-button">
            {loading ? <Loader2 className="animate-spin" size={18} /> : "CONTINUE TO PAYMENT"}
          </button>
        </div>
      </div>
    </form>
  );
};
